import Image from 'next/image'
import { FC, useMemo } from 'react'

import {
  FinanceBalanceState,
  FinanceBalanceTicketType
} from '@/api/models/finance'
import { cn } from '@/app/utils'
import { AppButton } from '@/components/shared/button'

import { ticketButtonText, ticketStyles } from '../../model/tickets.constants'
import { BalanceSummaryVariant } from '../../model/tickets.types'
import { getTicketImageProps } from '../../model/tickets.utils'
import BalanceTicketContent from './BalanceTicketContent'

type Props = {
  ticket: FinanceBalanceTicketType
  variant: BalanceSummaryVariant
  className?: string
  onButtonClick: () => void
  useCustomTitles: boolean
}

const BalanceTicket: FC<Props> = ({
  ticket,
  variant,
  className,
  onButtonClick,
  useCustomTitles
}) => {
  const ticketImageProps = getTicketImageProps(ticket)

  const buttonText = useMemo(() => {
    if (ticket.state === FinanceBalanceState.NotPaid && !ticket.canPay) {
      return ticketButtonText[FinanceBalanceState.AllPaid]
    }
    return ticketButtonText[ticket.state]
  }, [ticket.canPay, ticket.state])

  return (
    <article
      data-testid="balance-ticket-id"
      className={cn(
        'relative flex h-full flex-col justify-between overflow-hidden rounded-16 p-20 lg:p-16',
        ticketStyles[ticket.type].root,
        className
      )}
    >
      <div
        className={cn(
          'absolute object-contain',
          ticketImageProps.className[variant]
        )}
      >
        <Image src={ticketImageProps.src} fill alt="image" />
      </div>

      <BalanceTicketContent
        ticket={ticket}
        variant={variant}
        className="z-10"
        useCustomTitles={useCustomTitles}
      />

      <AppButton
        variant="secondary"
        className="w-[160px] lg:w-[140px]"
        onClick={onButtonClick}
      >
        {buttonText}
      </AppButton>
    </article>
  )
}

export default BalanceTicket
